import React, { useState, useEffect } from "react";
import { GroupType } from "pages/OrganizationAdmin/types";
import { Tables, showToast } from "@deeptrue-frontend/ui";
import Menu from "../Menu";
import styled from "styled-components/macro";

import { Button, Search } from "@deeptrue-frontend/ui";
import AddIcon from "@material-ui/icons/Add";
import { useDebounce } from "hooks/useDebouce";
import ModalWithTitle from "components/Modal/ModalWIthTitle";
import Forms from "components/Forms";
import { FormValues } from "components/Forms/Organization/AddGroup";
import api from "api/api";
import { FormikHelpers } from "formik";
import RouterProvider from "components/RouterProvider/RouterProvider";
import { RouteComponentProps } from "react-router-dom";
import queryString from "query-string";
import { defaultLimit } from "pages/OrganizationAdmin/Groups";
import Group from "./Group";
import { useFormatMessage } from "components/IntlProvider";

const { ROUTES, withRouter } = RouterProvider;
const { Table, TableBody, TableHeader, TablePagination, TableFooter } = Tables;

const AllGroups = styled.span`
  fontsize: 12px;
  color: gray;
`;

const AddButton = styled(Button)`
  display: flex;
  align-items: center;
  svg {
    margin-right: 0.5rem;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  width: 100%;
  align-items: center;
`;

const thCells = (t: any) => [
  {
    text: t("organization.columns.group", "Group"),
    orderName: "name",
    props: {
      width: "80%",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.resources", "Resources"),
    props: {
      width: "20%",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
];

interface Props extends RouteComponentProps {
  isLoaded: boolean;
  groups: GroupType[];
  count: number;
  organization: string;
  refetchGroups: () => void;
  currentPage: number;
  orderBy?: string;
  sortColumns: (colName: string) => void;
}

const UserGroups = ({
  isLoaded,
  groups,
  count,
  history,
  location,
  organization,
  refetchGroups,
  currentPage,
  orderBy,
  sortColumns,
}: Props) => {
  const [search, setSearchValue] = useState("");
  const [isModalAddGroup, setIsModalAddGroup] = useState(false);
  const searchValue = useDebounce(search);
  const parsedSearch = queryString.parse(location.search);
  const t = useFormatMessage();

  useEffect(() => {
    const params = {
      limit: defaultLimit,
      offset: 0,
      search: searchValue,
      ordering: parsedSearch.ordering,
    };
    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.GROUPS}?${queryString.stringify(params)}`
    );
    // eslint-disable-next-line
  }, [searchValue]);

  const addGroup = (
    values: FormValues,
    { setErrors }: FormikHelpers<FormValues>
  ) => {
    return api.resources.organization.groups
      .createGroup({
        organizationCodename: organization,
        groupData: values,
      })
      .then(() => {
        refetchGroups();
        setIsModalAddGroup(false);
        showToast({
          type: "success",
          title: t("organization.added", "{name} added", {
            name: t("organization.group", "Group"),
          }),
        });
      })
      .catch((err) => {
        setErrors(err.response.data);
      });
  };

  const onChangePage = (page: number) => {
    const params = {
      limit: defaultLimit,
      offset: defaultLimit * page - defaultLimit,
      search: searchValue,
      ordering: parsedSearch.ordering,
    };
    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.GROUPS}?${queryString.stringify(params)}`
    );
  };

  return (
    <>
      <ModalWithTitle
        open={isModalAddGroup}
        onClose={() => setIsModalAddGroup(false)}
        title={t("organization.add", "Add {name}", {
          name: t("organization.group", "Group"),
        })}
      >
        <Forms.AddGroupToOrganization
          onSubmit={addGroup}
          closeModal={() => setIsModalAddGroup(false)}
        />
      </ModalWithTitle>
      <Menu />
      <Search
        placeholder={t("organization.search", "Search...")}
        onChange={(e) => setSearchValue(e.target.value as string)}
      />

      <HeaderWrapper>
        <AllGroups>
          {count}{" "}
          {t("organization.groups", "{name} within organization", {
            name: t("organization.groups", "Groups"),
          })}
        </AllGroups>
        <AddButton variant="primary" onClick={() => setIsModalAddGroup(true)}>
          <AddIcon />
          <span>
            {t("organization.add", "Add {name}", {
              name: t("organization.group", "Group"),
            })}
          </span>
        </AddButton>
      </HeaderWrapper>
      <Table>
        <TableHeader
          cells={thCells(t)}
          orderBy={orderBy}
          onOrdering={sortColumns}
        />
        <TableBody
          withLoader
          isLoading={!isLoaded}
          isData={count > 0}
          noDataText={t(
            "noresults",
            "No results. We have nothing to show here."
          )}
        >
          {groups.map((group: GroupType) => {
            return (
              <Group
                key={group.id}
                groupData={group}
                organizationCodename={organization}
                refetchGroups={refetchGroups}
              />
            );
          })}
        </TableBody>
        <TableFooter>
          <TablePagination
            count={count}
            currentPage={currentPage}
            rowsPerPage={defaultLimit}
            onChangePage={onChangePage}
          />
        </TableFooter>
      </Table>
    </>
  );
};

export default withRouter(UserGroups);
